<template>
  <transition name="fade">
    <div class="AiEditorContainer" v-show="streamLoading">
      <div class="aiAvatarSelection">
        <!-- AVATAR ONLY LAYOUT -->
        <div class="fullscreenAvatar" style="" v-if="isAvatarOnly">
          <div
            class="avatarBackground"
            :style="'background-image:url(' + backgroundSelected?.url + ');'"
          ></div>
          <VideoAvatarPreview />
        </div>
        <!-- AVATAR BOTTOM LEFT LAYOUT -->
        <div class="bottomLeftAvatar" v-if="isAvatarBottomLeft || isVoiceOnly">
          <div class="uploadSlidePanel">
            <b-upload
              v-if="canUploadDocument"
              drag-drop
              class="uploadHoverPanel"
              @input="uploadAPdf"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.ppt,.pptx,.odt,.ods,.odp,.rtf,.csv,.txt,.xml,.html,.md,.key,.pages"
              :loading="isProceedSlideInProgress"
              :disabled="!isAvatarAvaialbleEnabled"
            >
              <div
                class="uploadPanelContainer"
                v-if="canUploadDocument && !isProceedSlideInProgress"
              >
                <div class="textUploadContainer">
                  <div class="firstElement">
                    {{ $t("aiAvatar.uploadLabelTitle") }}
                  </div>
                  <div class="secondElement">
                    {{ $t("aiAvatar.uploadLabelSubTitle") }}
                  </div>
                </div>
                <div class="uploadIcon">
                  <w-icon
                    size="xxlarge"
                    icon="file-upload-outline"
                    color="light2"
                  />
                </div>
              </div>
            </b-upload>

            <!-- WHEN DOCUMENT UPLOADED -->
            <div v-else class="fullFrame">
              <div v-if="isStaticDocumentAvailable">
                <img
                  alt="pdf uploaded"
                  :src="currentSlideImage"
                  style="max-width: 100%; max-height: 100%"
                />
                <div
                  class="deletePDFLayout"
                  :class="{ centerLayout: isVoiceOnly }"
                  @click="deleteDocument"
                >
                  <div>
                    <w-icon icon="file-document-remove-outline" size="large" />
                    <div>{{ $t("aiAvatar.removeDocument") }}</div>
                  </div>
                </div>
              </div>
              <div v-else-if="isVideoAvailable" class="fullFrame">
                <VideoChunckGenerator
                  :chunk-video-url="this.currentSlide.chunkVideoUrl"
                  :fileID="this.currentSlide.fileID"
                  :timestamp-start="this.currentSlide.videoTimestampStart"
                  :timestamp-end="this.currentSlide.videoTimestampEnd"
                  @video-metadata="onVideoMetadata"
                />
              </div>
              <div v-else>
                {{ $t("aiAvatar.errorLoadingOriginalDocument") }}
              </div>
            </div>
          </div>
          <transition name="zoom">
            <div class="stickerAvatar" v-show="isAvatarBottomLeft">
              <div
                class="avatarBackground"
                :style="'background-image:url(' + backgroundSelected?.url + ')'"
              ></div>
              <VideoAvatarPreview />
            </div>
          </transition>
        </div>
        <!-- SLIDE LAYOUT -->
        <AISlidePanel
          v-if="isSlide"
          :should-editable="true"
          id="slideContainerID"
        />

        <!-- Option Panel -->
        <transition name="fade">
          <AIOptionPanel
            :fileName="fileUploaded?.name"
            v-show="optionPanelVisibility"
            @close="deleteDocument"
            @validate="launchDocumentToLLM"
          />
        </transition>
        <!-- PANEL LOADING PDF/LLM -->
        <div
          class="loadingPDFPanel"
          v-if="isProceedSlideInProgress && !optionPanelVisibility"
        >
          <div class="loadingCard">
            <div class="processingLine">
              <w-icon
                icon="check-circle-outline"
                color="success"
                v-if="!proceedDocument"
              />
              <circle-loader size="small" v-else />
              <span class="labelProgressProcessing">{{
                $t("aiAvatar.loadingPDF")
              }}</span>
            </div>
            <div class="processingLine">
              <w-icon
                icon="check-circle-outline"
                color="success"
                v-if="!proceedDocument && getSlideToLLMProgression"
              />
              <circle-loader size="small" v-else />
              <span class="labelProgressProcessing">{{
                $t("aiAvatar.loadingLLM")
              }}</span>
            </div>
            <div class="processingLine">
              <w-icon icon="check-circle-outline" color="success" v-if="false" />
              <circle-loader size="small" v-else />
              <span
                class="labelProgressProcessing"
                v-if="!getSlideToLLMProgression"
                >{{ $t("aiAvatar.loadingSlides") }}</span
              >
              <span class="labelProgressProcessing" v-else>{{
                $t("aiAvatar.loadingSlidesNumber", {
                  slide: getSlideToLLMProgression,
                })
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        class="aiAvatarContentEdition"
        v-if="isContentEditingOpen && !isProceedSlideInProgress"
      >
        <AIAvatarContentEditionPanel
          ref="aiAvatarContentEdition"
          @generateAllSlide="generateAllSlide"
          @generateSlide="generatethisSlide"
          @errorDuringProcess="errorDuringProcess"
        />
      </div>

      <!-- Modal Waiting generation -->
      <w-modal :active="showModalWaiting" :fullscreen="true" :modal="true">
        <CenterCenter>
          <div class="panelWaiting">
            <div class="centerPanelWaiting">
              <div class="waitingLabel">
                {{ $t("aiAvatar.generatingSlide") }}
              </div>
              <b-progress
                class="progressWaiting"
                type="is-primary"
                show-value
                :value="(nbSlideGenerated / totalSlideToGenerate) * 100"
              >
                <div class="labelWaiting">
                  {{ nbSlideGenerated }} / {{ totalSlideToGenerate }}
                </div>
              </b-progress>
              <div
                class="successFeedback"
                v-if="nbSlideGenerated === totalSlideToGenerate"
              >
                <div class="iconCheck">
                  <w-icon icon="check-circle-outline" color="primary" />
                </div>
                <div class="labelSuccess">
                  {{ $t("aiAvatar.successFeedback") }}
                </div>
              </div>
              <w-button
                v-if="nbSlideGenerated < totalSlideToGenerate"
                class="buttonCancel"
                @click="onCancelProcess"
                color="outlined"
              >
                {{ $t("generic.cancel") }}
              </w-button>
              <w-button
                v-else
                class="buttonOk"
                color="primary"
                @click="finishProcess"
              >
                {{ $t("generic.ok") }}
              </w-button>
            </div>
          </div>
        </CenterCenter>
      </w-modal>
    </div>
  </transition>
</template>

<script lang="ts">
import store from "@/store";
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import { SourceVideoType } from "@/enum/SourceVideoEnum";

import {
  CLEAR_MAIN_MEDIAID,
  CLEAR_STICKER_MEDIAID,
  GENERATE_MAIN_MEDIAID,
  GENERATE_STICKERS_MEDIAID,
  RECORD_STICKER_POSITION,
  RECORD_STICKER_VIDEO_MUTE,
  SELECT_SEGMENT,
} from "@/store/recordingState/recordStateAction";
import VideoFilePlayer from "@/components/recorder/videoLayout/videoPlayer/VideoFilePlayer.vue";
import WSelect from "@/components/wrapper/w-select.vue";
import WButton from "@/components/wrapper/w-button.vue";
import WInput from "@/components/wrapper/w-input.vue";
import WIcon from "@/components/wrapper/w-icon.vue";
import UpgradePlanButton from "@/components/upgradePlan/UpgradePlanButton.vue";
import { isFreemium, MAX_LENGTH_AIAVATAR_PREMIUM } from "@/utils/workspaceUtil";
import {
  AiavatarGenerationModel,
  AvatarBackground,
  AvatarStyle,
  LIST_OF_AVATAR,
  LIST_OF_BACKGROUND,
} from "@/store/AIAvatar/aiavatarGenerationModel";
import { initWeetEditing } from "@/utils/createWeetUtil";
import { ChunkMedia, Media } from "@/store/media/mediaModel";
import {
  CREATE_CHUNK_MEDIA,
  CREATE_SLIDE_MEDIA,
  CREATE_VIDEO_SLIDE_FROM_CHUNK,
  REFRESH_MEDIA,
  UPDATE_METADATA,
} from "@/store/media/mediaAction";
import {
  ADD_TIME_EVENT,
  MOVE_SECTION_EVENT,
  REMOVE_SECTION_EVENT,
  SAVE_TIMELINE_IF_NEED,
  TIMELINE_UNSAVED,
} from "@/store/timeLine/timeLineAction";
import CenterCenter from "@/components/layout/CenterCenter.vue";
import {
  alertErrorWithConfirmation,
  alertMessageCircle,
  confirmation,
  inform,
} from "@/utils/dialog";
import { ADD_ANNOTATIONS_TO_REVIEW } from "@/store/annotation/annotationAction";
import WToolTip from "@/components/wrapper/w-toolTip.vue";

import {
  SpeachEvent,
  SpeachEventAvatarDuration,
  SpeachEventName,
} from "@/speach/speachEvent";
import delay from "delay";
import {
  ScriptSlideLayout,
  Slide,
  SlideAnnotation,
  SlideGenerationMode,
  SlideTemplate,
} from "@/store/slides2Video/slides2VideoModels";
import { createAutoAnnotations } from "@/utils/autoAnnotationUtil";
import {
  DOCUMENT_TO_SLIDE,
  INIT_SLIDES_IF_NEEDED,
  OPEN_PANEL_OPTIONS,
  RESET_SLIDES_2_VIDEO,
  SELECT_SLIDE,
  SLIDE_TO_LLM,
  SLIDES_GENERATION_AUTO_MODE,
  SLIDES_TO_LLM_IN_PROGRESS,
  UPDATE_SLIDES,
} from "@/store/slides2Video/slides2VideoAction";
import {
  AIAVATAR_GENERATE,
  AVATAR_EDITING_TEXT,
  AVATAR_GENERATING,
  OPEN_AVATAR_EDITING_CONTENT,
  SELECT_AI_ACCENT,
  SELECT_AI_AVATAR,
  SELECT_AI_BACKGROUND,
  SELECT_AI_LANG,
  SELECT_AI_SCRIPT_SLIDE_LAYOUT,
  SELECT_AI_VOICE,
} from "@/store/AIAvatar/AIAVatarAction";
import { StickerPosition } from "@/enum/StickerPositionEnum";
import AIAvatarContentEditionPanel from "@/components/recorder/aiAvatar/panel/AIAvatarContentEditionPanel.vue";
import WModal from "@/components/wrapper/w-modal.vue";
import { clone } from "@/utils/cloneUtil";
import { updateCurrentSlide } from "@/utils/slides/slideUtil";
import { accentsForLanguage } from "@/utils/voiceUtil";
import { getVoiceCatalog } from "@/store/media/ttsCatalogModel";
import AISlidePanel from "@/components/recorder/aiAvatar/panel/AISlidePanel.vue";
import { exportNode2JpgDataURL } from "@/utils/ImageUtil";
import AIOptionPanel from "@/components/recorder/aiAvatar/panel/AIOptionPanel.vue";
import { stringToBase64 } from "@/utils/util";
import { TimeEventType } from "@/enum/TimeEventType";
import {
  getFreeTimeAfterEvent,
  getTimeEventbeforeTime,
} from "@/utils/timeLineUtils";
import CircleLoader from "@/components/loader/CircleLoader.vue";
import VideoChunckGenerator from "@/components/videoImageBackground/VideoChunckGenerator.vue";
import { VideoMetadata } from "@/components/videoImageBackground/VideoChunckGenerator.vue";
import VideoAvatarPreview from "@/components/videoImageBackground/VideoAvatarPreview.vue";
import { informEndOfWeetGeneration, informWeetGenerationProgress } from "@/utils/autoMode/autoModeUtils";

@Component({
  components: {
    VideoAvatarPreview,
    VideoChunckGenerator,
    CircleLoader,
    AIOptionPanel,
    AISlidePanel,
    WModal,
    AIAvatarContentEditionPanel,
    WToolTip,
    CenterCenter,
    UpgradePlanButton,
    WIcon,
    WInput,
    WButton,
    WSelect,
    VideoFilePlayer,
  },
  computed: {},
})
export default class AIAvatarSource extends Vue {
  private showModalWaiting = false;
  private videoMetadata: VideoMetadata | null = null;
  private cancelProcess = false;
  private optionPanelVisibility = false;

  // value if the stream is setup
  private streamLoading: boolean = false;

  private nbSlideGenerated = 0;
  private totalSlideToGenerate = 0;

  private fileUploaded: File | null = null;

  private proceedDocument = false;

  get langSelected() {
    return store.getters.getAvatarLangSelected;
  }

  get accentSelected() {
    return store.getters.getAvatarAccentSelected;
  }

  get isContentEditingOpen() {
    return store.getters.isOpenAvatarEditionContent;
  }

  get slideLayout() {
    return store.getters.getAvatarScriptSlideLayout;
  }

  get canUploadDocument(): boolean {
    if (
      this.slideLayout === ScriptSlideLayout.SLIDE ||
      this.slideLayout === ScriptSlideLayout.AVATAR_ONLY
    ) {
      return false;
    } else {
      return !this.isStaticDocumentAvailable && !this.isVideoAvailable;
    }
  }

  get isVideoAvailable() {
    return this.currentSlide?.documentType === "VIDEO";
  }
  get isStaticDocumentAvailable() {
    return (
      this.currentSlide?.documentType === "PDF" &&
      this.currentSlide?.originalDocumentB64
    );
  }

  get isAvatarGenerating() {
    return store.getters.isAvatarGenerating;
  }

  get avatarSelected() {
    return store.getters.getAvatarSelected;
  }

  get maxlengthOfCharacter() {
    // if (this.isFreemium) {
    //   return MAX_LENGTH_AIAVATAR_FREEMIUM;
    // } else {
    //   return MAX_LENGTH_AIAVATAR_PREMIUM;
    // }
    return MAX_LENGTH_AIAVATAR_PREMIUM;
  }

  get getSlideToLLMProgression() {
    const slide = store.getters.getSlideToLLMProgression;
    if (slide > 0) {
      this.emitGenerationProgress(
        this.$t("aiAvatar.loadingSlidesNumber", { slide: slide }).toString()
      );
    }
    return slide;
  }

  get timeRemaining() {
    return store.getters.getRemainingAiCredits;
  }

  get isAIAvatarAuthorized() {
    return store.getters.isAvatarAI;
  }

  get isAvatarAvaialbleEnabled(): boolean {
    if (!this.isAIAvatarAuthorized) {
      return false;
    }
    if (this.timeRemaining <= 0) {
      return false;
    }

    return true;
  }

  get backgroundSelected() {
    const bgSelected = store.getters.getAvatarBabckgroundSelected;
    return (
      LIST_OF_BACKGROUND.find((bg) => {
        return bg.background === bgSelected;
      }) || LIST_OF_BACKGROUND[0]
    );
  }

  get isAvatarBottomLeft() {
    return this.slideLayout === ScriptSlideLayout.AVATAR_BOTTOM_LEFT;
  }

  get isAvatarOnly() {
    return this.slideLayout === ScriptSlideLayout.AVATAR_ONLY;
  }

  get isVoiceOnly() {
    return this.slideLayout === ScriptSlideLayout.VOICE_ONLY;
  }

  get isSlide() {
    return this.slideLayout === ScriptSlideLayout.SLIDE;
  }

  get isSlideGenerationAutoMode() {
    return store.getters.isSlideAutoMode;
  }

  get voiceSelected() {
    return store.getters.getAvatarVoiceSelected;
  }

  get textToGenerate() {
    return store.getters.getAiAvatarTextContent;
  }

  get isFreemium(): boolean {
    return isFreemium();
  }

  get isPanelOptionOpened() {
    return store.getters.isPanelOptionOpen;
  }
  get isImageGenerationInProgress() {
    return store.getters.isImageGenerationInProgress;
  }

  get isProceedSlideInProgress() {
    return store.getters.isProceedSlideInProgress;
  }

  get slides(): Slide[] {
    return store.getters.slides;
  }

  get currentSlide(): Slide {
    if (store.getters.getCurrentSlideIndex > -1) {
      return store.getters.slides[store.getters.getCurrentSlideIndex];
    }
    return new Slide();
  }

  get currentSlideImage(): string {
    if (this.currentSlide && this.currentSlide.originalDocumentB64) {
      return "data:image/jpg;base64," + this.currentSlide.originalDocumentB64;
    } else {
      return "";
    }
  }
  get avatarHeight() {
    if (this.slideLayout === ScriptSlideLayout.AVATAR_ONLY) {
      return 1080;
    }
    if (this.slideLayout === ScriptSlideLayout.SLIDE) {
      return 1080;
    }
    return 480;
  }

  get stickerPositionLayout() {
    if (this.slideLayout === ScriptSlideLayout.SLIDE) {
      if (this.currentSlide.slideTemplate === SlideTemplate.TITLE) {
        return StickerPosition.NONE;
      }
      if (this.currentSlide.slideTemplate === SlideTemplate.AVATAR_LEFT) {
        return StickerPosition.FULL_LEFT;
      }
      if (this.currentSlide.slideTemplate === SlideTemplate.AVATAR_RIGHT) {
        return StickerPosition.FULL_RIGHT;
      }
    }
    if (
      this.slideLayout === ScriptSlideLayout.AVATAR_ONLY ||
      this.slideLayout === ScriptSlideLayout.VOICE_ONLY
    ) {
      return StickerPosition.NONE;
    }

    return StickerPosition.BOTTOM_LEFT;
  }

  get isImageOrVideoGeneratedReady() {
    if (this.currentSlide.layout === ScriptSlideLayout.SLIDE) {
      if (
        this.currentSlide.slideTemplate === SlideTemplate.ILLUSTRATED_RIGHT ||
        this.currentSlide.slideTemplate === SlideTemplate.ILLUSTRATED_LEFT ||
        this.currentSlide.slideTemplate === SlideTemplate.TITLE_ILLUSTRATED ||
        this.currentSlide.slideTemplate === SlideTemplate.ILLUSTRATED_ONLY
      ) {
        if (
          this.currentSlide.promptIllustrationGeneration &&
          !this.currentSlide.slideImageB64 &&
          !this.currentSlide.slideVideoUrl
        ) {
          return false;
        }
      }
    } else if (
      this.currentSlide.layout === ScriptSlideLayout.AVATAR_BOTTOM_LEFT ||
      this.currentSlide.layout === ScriptSlideLayout.VOICE_ONLY
    ) {
      if (this.currentSlide.documentType === "VIDEO") {
        console.log("isImageOrVideoGeneratedReady", this.currentSlide);
        return !!this.currentSlide.chunkVideoUrl;
      }
    }

    return true;
  }

  @Watch("isProceedSlideInProgress")
  private launchAutoMode() {
    if (store.getters.isSlideAutoMode && !this.isProceedSlideInProgress) {
      this.generateAllSlide();
    }
  }
  private deleteDocument() {
    this.optionPanelVisibility = false;
    store.dispatch(SELECT_AI_SCRIPT_SLIDE_LAYOUT, ScriptSlideLayout.VOICE_ONLY);
    store.dispatch(SLIDES_TO_LLM_IN_PROGRESS, false);
    store.dispatch(SELECT_SEGMENT, null);
    store.dispatch(RESET_SLIDES_2_VIDEO);
  }

  @Watch("currentSlide")
  private onCurrentSlideChange() {
    if (this.currentSlide) {
      store.dispatch(SELECT_AI_SCRIPT_SLIDE_LAYOUT, this.currentSlide.layout);
      const defaultBackground = LIST_OF_BACKGROUND.find((bg) => {
        return bg.background === AvatarBackground.WHITE;
      });
      store.dispatch(
        SELECT_AI_BACKGROUND,
        this.currentSlide.avatarBackground ||
          this.backgroundSelected?.background ||
          defaultBackground
      );
      store.dispatch(
        SELECT_AI_AVATAR,
        this.currentSlide.avatar || this.avatarSelected
      );

      // fix current Slide (propagate previous selection if empty)
      // waiting all Watch finished
      this.$nextTick(() => {
        if (!this.currentSlide) {
          return;
        }
        let isModified = false;
        const newSlide = clone(this.currentSlide) as Slide;

        // propagate backgroudn if need
        if (!this.currentSlide.avatarBackground && this.backgroundSelected) {
          newSlide.avatarBackground = this.backgroundSelected.background;
          isModified = true;
        }
        // propagate avatar if need
        if (!this.currentSlide.avatar && this.avatarSelected) {
          newSlide.avatar = this.avatarSelected;
          isModified = true;
        }
        // propagate lang if need
        if (!this.currentSlide.lang && this.langSelected) {
          newSlide.lang = this.langSelected;
          isModified = true;
        }
        // propage voice
        if (!this.currentSlide.voice && this.voiceSelected) {
          const gender = LIST_OF_AVATAR.find((av) => {
            return av.avatar === this.avatarSelected;
          })?.gender;
          // Default to a free (non-advanced/Microsoft) voice for the gender, so nobody is billed by
          // accident. Keep the length guard so a gender with no voice never leaves the slide
          // voice-less and re-triggers this watcher forever.
          const catalog = getVoiceCatalog(store.getters.isAdvancedTtsEnabled);
          const available = catalog.voices.filter((v) => v.gender === gender);
          if (available.length) {
            const isVoiceAvaialbleForGender = available.some(
            (v) => v.id === this.voiceSelected
          );
            const fallback = available.find((v) => !v.advanced) || available[0];
            newSlide.voice = isVoiceAvaialbleForGender ? this.voiceSelected : fallback.id;
            isModified = true;
          }
        }
        if (isModified) {
          updateCurrentSlide(newSlide);
        }
      });
    }
  }

  @Watch("isContentEditingOpen")
  private onContentEditingOpen() {
    if (this.slides && this.slides.length === 0) {
      store.dispatch(
        SELECT_AI_SCRIPT_SLIDE_LAYOUT,
        ScriptSlideLayout.AVATAR_ONLY
      );
    }
  }

  @Watch("backgroundSelected")
  private onBackgroundSelected(bg) {
    if (this.currentSlide && bg) {
      const newSlide = clone(this.currentSlide) as Slide;
      newSlide.avatarBackground = bg.background;
      updateCurrentSlide(newSlide);
    }
  }

  @Watch("avatarSelected")
  private onAvatarSelected() {
    if (this.currentSlide) {
      const newSlide = clone(this.currentSlide) as Slide;
      newSlide.avatar = this.avatarSelected;
      updateCurrentSlide(newSlide);
    }
  }

  public async mounted() {
    this.streamLoading = true;
    // setup favorite avatar
    if (store.getters.getFavAvatar) {
      store.dispatch(SELECT_AI_AVATAR, store.getters.getFavAvatar);
    }
    // setup Favorite background
    if (store.getters.getFavAvatarBackground) {
      store.dispatch(
        SELECT_AI_BACKGROUND,
        store.getters.getFavAvatarBackground
      );
    }
    // setup lang
    if (store.getters.getFavAvatarLang) {
      store.dispatch(SELECT_AI_LANG, store.getters.getFavAvatarLang);
    }

    store.dispatch(SELECT_AI_SCRIPT_SLIDE_LAYOUT, ScriptSlideLayout.VOICE_ONLY);

    // the component is re-created on every source switch: only create a slide when
    // there is none, so a deck built earlier survives a round trip to another mode
    store.dispatch(INIT_SLIDES_IF_NEEDED);

    const catalog = getVoiceCatalog(store.getters.isAdvancedTtsEnabled);

    // Restore the favorite voice unconditionally, including advanced (paid) voices. If there are no credits
    // left, anyway we can't reach here: we can't generate TTS, even normal free voices
    const favVoice = store.getters.getFavAvatarVoice;
    if (favVoice && catalog.voices.some((v) => v.id === favVoice)) {
      store.dispatch(SELECT_AI_VOICE, favVoice);
    }

    // Restore the favorite accent only if it applies to the current language, else reset to none.
    const favAccent = store.getters.getFavAvatarAccent;
    const lang = store.getters.getAvatarLangSelected;
    if (favAccent && accentsForLanguage(lang).some((a) => a.value === favAccent)) {
      store.dispatch(SELECT_AI_ACCENT, favAccent);
    } else {
      store.dispatch(SELECT_AI_ACCENT, null);
    }
  }

  private async initWeetEditing() {
    if (store.getters.getEditingWeetID === "") {
      await initWeetEditing();
    }
  }

  private showOptionPanel() {
    this.optionPanelVisibility = true;
    store.dispatch(SLIDES_TO_LLM_IN_PROGRESS, true);
    store.dispatch(OPEN_PANEL_OPTIONS, true);
  }
  private closeOptionPanel() {
    this.optionPanelVisibility = false;
    store.dispatch(OPEN_PANEL_OPTIONS, false);
  }

  private async uploadAPdf(file: File) {
    store.dispatch(SELECT_SEGMENT, null);
    this.fileUploaded = file;
    // launch option Panel
    this.showOptionPanel();
  }

  private async launchDocumentToLLM() {
    this.closeOptionPanel();
    // open the editino Panel (when ready)
    store.dispatch(OPEN_AVATAR_EDITING_CONTENT, true);
    // Convert PDF to Slide
    this.proceedDocument = true;
    this.emitGenerationProgress(this.$t("aiAvatar.loadingPDF")+"");
    await store.dispatch(DOCUMENT_TO_SLIDE, this.fileUploaded);
    this.proceedDocument = false;

    // launch LLM if need
    if (store.getters.getGenerationMode !== SlideGenerationMode.NONE) {
      this.emitGenerationProgress(this.$t("aiAvatar.loadingLLM")+"");

      await store.dispatch(SLIDE_TO_LLM, {
        slides: store.getters.slides,
        hiddenB64Instruction: stringToBase64(
          store.getters.getCustomInstruction
        ),
        lang: store.getters.getAvatarLangSelected,
        generationMode: store.getters.getGenerationMode,
      });
    } else {
      store.dispatch(SLIDES_TO_LLM_IN_PROGRESS, false);
    }

    if (this.slides && this.slides.length >= 60) {
      alertMessageCircle(
        this.$t("aiAvatar.tooMuchSlide").toString(),
        this.$t("aiAvatar.tooMuchSlideTitle").toString()
      );
    }
  }

  /**
   * Emit a human-readable generation-progress message to BOTH the parent window
   * (SpeachEvent — existing behaviour) AND window.__generationProgress, which the
   * headless speach-generator worker polls to log / store the latest step.
   */
  private emitGenerationProgress(message: string) {
    window.parent.postMessage(
      new SpeachEvent(SpeachEventName.GENERATION_PROGRESS, message),
      "*"
    );
    informWeetGenerationProgress(message);
  }

  private getAvatarContentEditingComponent() {
    return this.$refs["aiAvatarContentEdition"] as AIAvatarContentEditionPanel;
  }

  private stopPreviewVoice() {
    this.getAvatarContentEditingComponent()?.stopPreviewVoice();
  }

  private shouldForceAvatarOnly() {
    if (this.slideLayout === ScriptSlideLayout.AVATAR_ONLY) {
      return true;
    }
    if (
      this.slideLayout === ScriptSlideLayout.AVATAR_BOTTOM_LEFT ||
      this.slideLayout === ScriptSlideLayout.VOICE_ONLY
    ) {
      if (!this.currentSlide.originalDocumentB64 && !this.currentSlide.fileID) {
        return true;
      }
    }
    return false;
  }

  private shouldGenerateVoiceOnly() {
    if (this.slideLayout === ScriptSlideLayout.VOICE_ONLY) {
      return true;
    }

    if (this.slideLayout === ScriptSlideLayout.SLIDE) {
      if (
        this.currentSlide.slideTemplate === SlideTemplate.TITLE ||
        this.currentSlide.slideTemplate === SlideTemplate.TITLE_ILLUSTRATED ||
        this.currentSlide.slideTemplate === SlideTemplate.ILLUSTRATED_LEFT ||
        this.currentSlide.slideTemplate === SlideTemplate.ILLUSTRATED_RIGHT ||
        this.currentSlide.slideTemplate === SlideTemplate.ILLUSTRATED_ONLY
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Generate All media need for the section
   * This media can be Avatar/voice with slide (video slide is legacy)
   * or Avatar Only
   */
  public async launchAvatarGeneration() {
    if (!this.currentSlide) {
      return;
    }
    this.stopPreviewVoice();
    if (this.isAvatarGenerating) {
      //if not step loading
      return;
    }

    /**
     * 1 - Text verification
     */
    // Check maxLength of text
    if (this.textToGenerate) {
      if (this.textToGenerate.length > this.maxlengthOfCharacter) {
        if (this.isFreemium) {
          try {
            await confirmation(
              this.$t("aiAvatar.freemiumCharacterLimitation").toString(),
              this.$t("aiAvatar.freemiumCharacterLimitationTitle").toString(),
              this.$t("generic.upgrade").toString()
            );
            this.$router.push({ name: "MyWorkspacePayment" });
          } catch (e) {
            // nothing to do when cancel
          }
        } else {
          alertMessageCircle(
            this.$t("aiAvatar.characterLimitation").toString(),
            this.$t("aiAvatar.freemiumCharacterLimitationTitle").toString()
          );
        }
        return;
      }

      try {
        await this.initWeetEditing(); // obtain a weet ID if need

        let mediaAvatar = new Media();
        const audioMetadata =
          await this.getAvatarContentEditingComponent().getAudioInformationForText();
        const seconds = audioMetadata.seconds;
        const audioBlob = audioMetadata.blob;

        // Compute the ideal duration
        let videoChunkDuration = -1;
        if (
          this.slideLayout === ScriptSlideLayout.AVATAR_BOTTOM_LEFT ||
          (this.slideLayout === ScriptSlideLayout.VOICE_ONLY &&
            this.currentSlide.fileID)
        ) {
          videoChunkDuration =
            this.currentSlide.videoTimestampEnd -
            this.currentSlide.videoTimestampStart;
        }

        const segmentSelected = store.getters.getSelectedSegment;
        store.dispatch(SELECT_SEGMENT, null);

        // waiting image or video generation
        const timeout = 180 * 1000;
        let waitingTime = 0;
        while (!this.isImageOrVideoGeneratedReady) {
          await delay(500);
          waitingTime += 500;
          if (waitingTime > timeout) {
            throw new Error("Image or video generation timeout");
          }
        }

        /**
         * 2 - Define if the section should be Avatar Only
         */
        if (this.shouldForceAvatarOnly()) {
          // Avatar Only
          store.dispatch(
            SELECT_AI_SCRIPT_SLIDE_LAYOUT,
            ScriptSlideLayout.AVATAR_ONLY
          );
          await store.dispatch(CLEAR_STICKER_MEDIAID);
          await store.dispatch(GENERATE_MAIN_MEDIAID);
          // create a media
          mediaAvatar.mediaID = store.getters.getMainMediaId;
          mediaAvatar.type = SourceVideoType.AIAVATAR;
        } else {
          // else create 2 mediaID
          await store.dispatch(CLEAR_STICKER_MEDIAID);
          await store.dispatch(CLEAR_MAIN_MEDIAID);
          await store.dispatch(GENERATE_MAIN_MEDIAID);
          await store.dispatch(GENERATE_STICKERS_MEDIAID);
          // setup StickerLayout
          mediaAvatar.mediaID = store.getters.getStickersMediaId;
          mediaAvatar.type = SourceVideoType.AIAVATAR;
        }

        // compute height of avatar (quality)
        const height = this.avatarHeight;

        // compute sticker Layour
        const stickerPosition = this.stickerPositionLayout;
        await store.dispatch(RECORD_STICKER_POSITION, stickerPosition);

        /**
         * 3 - In case of modification, erase old section
         */
        // Erase old section in case of replace (before generating new section)
        if (segmentSelected) {
          store.dispatch(REMOVE_SECTION_EVENT, segmentSelected.start.time);
        }

        const currentTimeBeforeInsertion = store.getters.getTimeLineDuration;

        /**
         * 4 - Launch avatar Genreration of only voice
         */
        // Launch avatar generation
        if (!this.shouldGenerateVoiceOnly()) {
          var params = new AiavatarGenerationModel(
            this.textToGenerate,
            this.avatarSelected,
            this.voiceSelected,
            this.langSelected,
            AvatarStyle.STATIC,
            this.backgroundSelected?.url || LIST_OF_BACKGROUND[0].url,
            // advanced avatar = V3 (p-video-avatar) with the same avatar image
            store.getters.isAvatarAdvanced ? 3 : 2
          );
          params.gesture = store.getters.getAvatarGestureSelected;
          params.accent = this.accentSelected;
          params.finalOutputHeight = height;
          params.minDuration = videoChunkDuration;
          if (this.isAvatarOnly) {
            params.slide = this.currentSlide;
          }
          mediaAvatar = await store.dispatch(AIAVATAR_GENERATE, {
            media: mediaAvatar,
            audioBlob: audioBlob,
            aiavatarParams: params,
          });
          await store.dispatch(TIMELINE_UNSAVED);
          await store.dispatch(REFRESH_MEDIA, mediaAvatar);

          // send time to speach (stat Speach)
          const dataEvent = new SpeachEventAvatarDuration(
            store.getters.getEditingWeetID,
            seconds
          );
          window.parent.postMessage(
            new SpeachEvent(SpeachEventName.AIAVATAR_DURATION, dataEvent),
            "*"
          );
        } else {
          // generate media Audio only
          const chunk = new ChunkMedia();
          chunk.blob = audioBlob;
          chunk.type = SourceVideoType.UPLOAD;
          chunk.mediaID = mediaAvatar.mediaID;
          chunk.partNumber = 1;
          chunk.weetID = store.getters.getEditingWeetID;
          chunk.lastPart = true;
          chunk.transcoding = true;
          chunk.transcriptB64 = stringToBase64(this.textToGenerate || "");
          chunk.slideB64 = this.currentSlide?.originalDocumentB64 || "";
          chunk.minDuration = videoChunkDuration;
          chunk.voice = this.voiceSelected;

          // start generating (allow to create an event MEDIA_PLAY)
          store.dispatch(AVATAR_GENERATING, true);
          store.dispatch(RECORD_STICKER_VIDEO_MUTE, true);
          store.dispatch(RECORD_STICKER_POSITION, StickerPosition.NONE);
          await delay(200);
          store.dispatch(CREATE_CHUNK_MEDIA, chunk);
          // waiting mediaChunk starting
          await delay(500);
          // reset configuration after voice only avatar created
          store.dispatch(RECORD_STICKER_VIDEO_MUTE, false);
          store.dispatch(RECORD_STICKER_POSITION, StickerPosition.BOTTOM_LEFT);
          store.dispatch(AVATAR_GENERATING, false);
        }

        // compute duration of the section base on voice or video chunk to compute timeline
        if (videoChunkDuration > seconds) {
          const metadMediaMetadata = {
            status: "WAITING",
            duration: videoChunkDuration,
          };
          await store.dispatch(UPDATE_METADATA, metadMediaMetadata);
        } else {
          const metadMediaMetadata = { status: "WAITING", duration: seconds };
          await store.dispatch(UPDATE_METADATA, metadMediaMetadata);
        }

        /**
         * 5 - Save slide based on document
         */
        // GENERATE SLIDE from Document/VIDEO
        if (
          this.slideLayout === ScriptSlideLayout.AVATAR_BOTTOM_LEFT ||
          this.slideLayout === ScriptSlideLayout.VOICE_ONLY
        ) {
          // GENERATE FROM PDF PAGE
          if (this.currentSlide.originalDocumentB64) {
            let mediaVideoSlide = new Media();
            mediaVideoSlide.type = SourceVideoType.SLIDE;
            mediaVideoSlide.mediaID = store.getters.getMainMediaId;

            const currentSlide = clone(this.currentSlide) as Slide;
            currentSlide.originalDocumentB64 = this.currentSlideImage;
            currentSlide.exportStaticB64 = this.currentSlideImage;
            currentSlide.slideTitle =
              currentSlide.slideTitle || this.fileUploaded?.name || "";
            currentSlide.slideDescription = currentSlide.slideDescription || "";
            currentSlide.slideTheme =
              currentSlide.slideTheme ||
              store.getters.getFavSlideTheme ||
              store.getters.getThemeColor;
            currentSlide.slideBackgroundColor =
              currentSlide.slideBackgroundColor ||
              store.getters.getFavSlideBackgroundColor ||
              "";
            currentSlide.slideTemplate = SlideTemplate.NONE;
            currentSlide.promptIllustrationGeneration = "";

            mediaVideoSlide = await store.dispatch(CREATE_SLIDE_MEDIA, {
              media: mediaVideoSlide,
              slide: currentSlide,
            });

            await store.dispatch(TIMELINE_UNSAVED);
            await delay(2000);
            await store.dispatch(REFRESH_MEDIA, mediaVideoSlide);
          }
          // OR GENERATE FROM FILE CHUNK
          if (this.currentSlide.fileID) {
            let mediaVideo = new Media();
            mediaVideo.type = SourceVideoType.VIDEO_SLIDE;
            mediaVideo.mediaID = store.getters.getMainMediaId;

            const currentSlide = clone(this.currentSlide) as Slide;

            await store.dispatch(TIMELINE_UNSAVED);
            mediaVideo = await store.dispatch(CREATE_VIDEO_SLIDE_FROM_CHUNK, {
              media: mediaVideo,
              slide: currentSlide,
              minDuration: seconds,
            });
            await store.dispatch(TIMELINE_UNSAVED);

            await delay(2000);
            await store.dispatch(REFRESH_MEDIA, mediaVideo);
          }
        }

        /**
         * 6 - Save slide based on HTML
         */
        // Generate Slide HTML
        if (this.slideLayout === ScriptSlideLayout.SLIDE) {
          let mediaSlide = new Media();
          mediaSlide.type = SourceVideoType.SLIDE_HTML;
          mediaSlide.mediaID = store.getters.getMainMediaId;

          // screenshot the slide
          const slideNode = document.getElementById("slideContainerID");
          if (slideNode == null) {
            throw new Error("Impossible is null, can't export slide");
          }
          const dataImage = await exportNode2JpgDataURL(
            slideNode,
            0.8,
            4,
            "#FFFFFF"
          );
          const newSlide = clone(this.currentSlide) as Slide;
          newSlide.originalDocumentB64 = this.currentSlideImage;
          newSlide.exportStaticB64 = dataImage;

          mediaSlide = await store.dispatch(CREATE_SLIDE_MEDIA, {
            media: mediaSlide,
            slide: newSlide,
          });

          await store.dispatch(TIMELINE_UNSAVED);
          await delay(2000);
          await store.dispatch(REFRESH_MEDIA, mediaSlide);
        }

        // clear text
        await store.dispatch(AVATAR_EDITING_TEXT, "");

        // Create auto annotations from slide
        if (this.currentSlide) {
          await this.createAutoAnnotation(
            this.currentSlide,
            currentTimeBeforeInsertion
          );
        }

        if (this.currentSlide) {
          // Now add a chapter
          if (this.currentSlide.slideTitle) {
            const timeEventChapter = clone(
              getTimeEventbeforeTime(
                store.getters.getTimeEvent,
                currentTimeBeforeInsertion,
                TimeEventType.MEDIA_PLAY
              )
            );
            timeEventChapter.time = getFreeTimeAfterEvent(
              store.getters.getTimeEvent,
              currentTimeBeforeInsertion
            );
            timeEventChapter.type = TimeEventType.CHAPTER_TITLE;
            timeEventChapter.value = this.currentSlide.slideTitle;
            await store.dispatch(ADD_TIME_EVENT, timeEventChapter);
          }
          if (this.currentSlide.order < this.slides.length) {
            store.dispatch(SELECT_SLIDE, this.currentSlide.order);
          } else {
            store.dispatch(RESET_SLIDES_2_VIDEO);
          }
        }

        await store.dispatch(AVATAR_GENERATING, false);
        await store.dispatch(TIMELINE_UNSAVED);
        await store.dispatch(SAVE_TIMELINE_IF_NEED);

        // Now move the new section
        if (segmentSelected) {
          store.dispatch(MOVE_SECTION_EVENT, {
            startSection: currentTimeBeforeInsertion,
            moveTo: segmentSelected.start.time,
          });
        }

        this.$emit("close");
      } catch (e) {
        console.error(e);
        // this.loadingVoice = false;
        store.dispatch(AVATAR_GENERATING, false);
        alertErrorWithConfirmation(
          this.$t("aiAvatar.content.error").toString()
        ).then(() => {
          this.$emit("close");
        });
      }
    }
  }

  public onVideoMetadata(metadata: VideoMetadata) {
    this.videoMetadata = metadata;
  }

  /**
   * Create annotations on the weet based on slide.annotations
   */
  public async createAutoAnnotation(slide: Slide, sectionStartTime: number) {
    const createdIDs = await createAutoAnnotations(
      slide,
      sectionStartTime,
      this.videoMetadata
    );
    if (createdIDs.length > 0) {
      await store.dispatch(ADD_ANNOTATIONS_TO_REVIEW, createdIDs);
      inform(
        this.$t(
          "createWeetComponent.advancedEditing.annotationMode.autoAnnotationsAdded",
          { count: createdIDs.length }
        ).toString()
      );
    }
  }

  public async onCancelProcess() {
    this.cancelProcess = true;
    this.showModalWaiting = false;
    this.totalSlideToGenerate = 0;
    this.nbSlideGenerated = 0;
  }
  public async errorDuringProcess(error: string) {
    alertErrorWithConfirmation(error).then(() => {
      this.onCancelProcess();
    });
  }
  public finishProcess() {
    this.showModalWaiting = false;
    this.cancelProcess = false;
    this.showModalWaiting = false;
    this.totalSlideToGenerate = 0;
    this.nbSlideGenerated = 0;
  }

  public async generateAllSlide() {

    this.totalSlideToGenerate = this.slides.length;
    this.showModalWaiting = true;
    this.cancelProcess = false;
    this.nbSlideGenerated = 0;
    for (const slide of this.slides) {
      await delay(100);
      if (!this.cancelProcess) {
        // Select THIS slide BEFORE generating it. Previously SELECT_SLIDE was done AFTER
        // launchAvatarGeneration, so the current slide lagged by one: slide 0 was generated twice
        // and the last slide never was.
        await store.dispatch(SELECT_SLIDE, slide.order-1);
        // Publish progress for the headless generation worker (and its UI).
        this.emitGenerationProgress(
          this.$t("aiAvatar.generateVideo", { slide: this.nbSlideGenerated + 1 }) +
            " (" + (this.nbSlideGenerated + 1) + "/" + this.totalSlideToGenerate + ")"
        );
        await this.launchAvatarGeneration();
        // Wait for THIS section's explainer video to finish before moving on. Otherwise the loop
        // advances as soon as the IMAGE is ready (isImageOrVideoGeneratedReady) and
        // __endAutoGeneration is set while later sections' videos are still rendering — so only the
        // first section ends up in the captured weet. No-op when no video is generated.
        await this.waitForSectionVideoGeneration();
        this.nbSlideGenerated++;
      }
    }
    // End of loop slide
    if (this.isSlideGenerationAutoMode) {
      store.dispatch(SLIDES_GENERATION_AUTO_MODE, false);
      this.closeRecorder();
    }
    // Inform the backend that ALL sections are ready.
    informEndOfWeetGeneration();
    this.emitGenerationProgress("");
  }

  /**
   * Waits for the CURRENT section's explainer video to be produced before moving on. Only illustration
   * slides (`promptIllustrationGeneration`) in auto-video mode generate a section video, so nothing else
   * waits. Deterministic completion instead of the transient in-progress flag (which was raced and
   * missed): resolve when the slide gets its `slideVideoUrl` (success), when the video attempt started
   * then ended without a URL (failure / fallback to image), or on a generous timeout — never hangs.
   */
  private async waitForSectionVideoGeneration() {
    if (!store.getters.getAutoVideoGeneration || this.cancelProcess) {
      return;
    }
    const slide = this.currentSlide;
    // No illustration prompt → no section video is generated for this slide → nothing to wait for.
    if (!slide || !slide.promptIllustrationGeneration) {
      return;
    }
    const timeout = 6 * 60 * 1000; // matches the video model's own timeout, with a margin
    let waited = 0;
    let started = false;
    while (waited < timeout && !this.cancelProcess) {
      const current = this.currentSlide;
      if (current && current.slideVideoUrl) {
        return; // video ready
      }
      const inProgress = store.getters.isVideoGenerationInProgress;
      if (inProgress) {
        started = true;
      } else if (started) {
        return; // the video attempt finished without a URL (failed / fell back to image)
      }
      await delay(500);
      waited += 500;
    }
  }
  private closeRecorder() {
    this.$emit("closeRecorder");
  }

  public async generatethisSlide() {
    this.showModalWaiting = true;
    this.cancelProcess = false;
    this.nbSlideGenerated = 0;
    this.totalSlideToGenerate = 1;
    await this.launchAvatarGeneration();
    this.nbSlideGenerated++;
  }

  beforeDestroy() {
    this.stopPreviewVoice();
  }
}
</script>

<style lang="scss">
// overload componennt
.uploadSlidePanel {
  .upload .upload-draggable {
    border-radius: 24px;
  }
}
</style>

<style lang="scss" scoped>
@import "@/scss/shadows.scss";

.AiEditorContainer {
  display: flex;
  width: 100%;
  height: 100%;
  animation-duration: 1s;
  border-radius: 8px;

  .aiAvatarSelection {
    width: 100%;
    height: 100%;
    min-height: 100%;
    max-height: 100%;
    background: var(--white);
    border: solid 1px var(--light);
    border-radius: 24px;

    .avatarParameter {
      width: 232px;
      text-align: right;
      position: absolute;
      left: -300px;
      top: 48px;
      padding: 16px;
      background: rgba(255, 255, 255, 0.6);
      backdrop-filter: blur(12px);
      border-radius: 16px;

      .paramsSelect {
        margin-bottom: 24px;
        width: 200px;
      }

      .buttonNextStep {
        position: absolute;
        right: 16px;
        bottom: 16px;
      }

      .labelSelect {
        position: relative;
        text-align: left;
        font-size: 14px;
        color: var(--dark);
        justify-content: space-between;
        align-items: flex-end;
        display: flex;
      }
    }

    .avatarBackground {
      position: absolute;
      width: 100%;
      left: 0px;
      height: 100%;
      background-size: cover;
      border-radius: 8px;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        border-radius: 8px;
      }
    }

    .bottomLeftAvatar {
      width: 100%;
      height: 100%;

      .uploadSlidePanel {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        .uploadHoverPanel {
          width: 100%;
          height: 100%;
          display: flex;
          .uploadPanelContainer {
            display: flex;
            height: 100%;
            width: 100%;
            align-items: center;
            justify-content: center;

            .textUploadContainer {
              text-align: left;
              margin-left: 32px;
              padding-right: 0px;
              .firstElement {
                font-size: 24px;
                font-weight: 900;
              }
              .secondElement {
                margin-top: 8px;
                font-size: 16px;
                font-weight: 600;
                color: var(--light2);
              }
            }
            .uploadIcon {
              margin-right: 128px;
              margin-left: 128px;
            }
          }
        }

        // the uploaded document must stay inside the 16/9 panel: without a
        // definite height here, the image renders at its natural size
        .fullFrame {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;

          > div {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
          }
        }

        .deletePDFLayout {
          position: absolute;
          width: 100%;
          height: 100%;
          background: rgba(10, 8, 47, 0.8);
          top: 0;
          left: 0;
          border-radius: 12px;
          display: flex;
          opacity: 0;
          align-items: center;
          justify-content: center;
          color: white;
          flex-direction: row;
          cursor: pointer;
          padding-left: 200px;
          transition: opacity 0.2s ease-in-out;

          &:hover {
            opacity: 1;
          }
        }
      }
    }

    .stickerAvatar {
      position: absolute;
      width: 180px;
      height: 180px;
      left: 16px;
      bottom: 16px;
      overflow: hidden;
      animation-duration: 0.2s;
      border-radius: 100% 100% 100% 12px;
      box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);

      .avatarPreview {
        border: 0px;
        background-size: 180%;
        background-position: 56% center;
      }
    }
  }
  .backgroundSelector,
  .avatarSelector {
    background: white;
    border-radius: 8px;
    padding: 2px 8px;
    margin-top: 4px;
    border: solid 1px var(--light1);
    min-width: 200px;
    font-size: 12px;
    cursor: pointer;
    color: var(--dark);

    .backgroundSelectorContent,
    .avatarSelectorContent {
      text-align: left;
      display: flex;
      justify-content: space-between;
      align-items: center;
      text-transform: capitalize;
    }
  }

  .backgroundSelection,
  .avatarSelection {
    display: flex;
    align-items: center;

    .backgroundSelectionImage,
    .avatarSelectionImage {
      border: 1px solid var(--light1);
      display: block;
      width: 32px;
      height: 32px;
      background: var(--light);
      background-position: center center;
      margin-right: 16px;
      border-radius: 4px;
      background-size: cover;
    }

    .backgroundSelectionLabel,
    .avatarSelectionLabel {
      font-size: 14px;
      flex: 1;
      color: var(--dark);
    }

    .backgroundSelected,
    .avatarSelectionSelected {
      margin-right: 0px;
    }
  }

  .panelWaiting {
    background: white;
    width: 534px;
    min-height: 100px;
    padding-top: 32px;
    padding-bottom: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    margin: auto;

    .centerPanelWaiting {
      width: calc(100% - 128px);

      .buttonCancel,
      .buttonOk {
        float: right;
      }

      .waitingLabel {
        text-align: left;
        font-weight: 900;
        font-size: 20px;
        margin-bottom: 16px;
      }

      .progressWaiting {
        width: 100%;

        .labelWaiting {
          font-weight: 700;
          font-size: 14px;
        }
      }

      .successFeedback {
        display: flex;

        .iconCheck {
          margin-right: 9px;
          display: flex;
          align-items: center;
        }

        .labelSuccess {
          flex: 1;
          justify-content: flex-start;
          align-items: center;
        }
      }
    }
  }

  .aiAvatarContentEdition {
    position: fixed;
    top: 88px;
    right: 16px;
    // 72px header + 16px margin on top, 80px timeline + 16px + 16px below
    height: calc(100vh - 200px);
    min-width: 320px;
    max-width: 320px;
    padding: 16px;
    background: var(--white);
    border: solid 1px var(--light1);
    border-radius: 24px;
    overflow-y: auto;
    z-index: 10;
  }

  // covers the whole recorder, like the option panel
  .loadingPDFPanel {
    position: fixed;
    display: flex;
    align-items: center;
    top: 0;
    left: 0;
    justify-content: center;
    background: var(--light);
    padding: 16px;
    width: 100%;
    height: 100%;
    z-index: 40;

    .loadingCard {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding: 32px 48px;
      background: var(--white);
      border-radius: 24px;
      box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
    }

    .processingLine {
      display: flex;
      width: 200px;
      align-items: center;
      justify-content: flex-start;
      font-size: 16px;
      margin-bottom: 16px;
      color: var(--light2);
      .labelProgressProcessing {
        margin-left: 16px;
      }
    }
  }
}
</style>
